<template>
  <v-card class="mb-4" :loading="loading" outlined>
    <v-toolbar color="titleBar" dark flat dense>
      <v-toolbar-title>{{ $t("call forwards") }}</v-toolbar-title>
      <v-spacer />
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn
            :disabled="loading"
            :loading="loading"
            v-on="on"
            @click="
              expanded = [];
              showAddCallForward = !showAddCallForward;
            "
            icon
          >
            <v-icon>mdi-playlist-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("add new call forward") }}</span>
      </v-tooltip>
    </v-toolbar>
    <v-card-text>
      <call-forward
        v-if="showAddCallForward"
        v-model="newItem"
        :product-id="productId"
        :empty-call-forward="emptyCallForward"
        :types="types"
        :numbers="numbers"
        @cancel="
          showAddCallForward = false;
          newItem = { ...emptyCallForward };
        "
        @created="
          getItems();
          expanded = [];
          showAddCallForward = false;
          newItem = { ...emptyCallForward };
        "
      />
      <data-table-extended
        v-else
        :headers="headers"
        :items="items"
        :loading="loading"
        :mobile-breakpoint="mbreakpoint"
        sort-by="id"
        sort-desc
        :expanded="expanded"
        show-expand
        :hide-default-footer="items.length <= 15"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [15, 50],
        }"
      >
        <template v-slot:item.disabled="{ headers, item }">
          <boolean-value :value="!item.disabled" />
        </template>
        <template v-slot:item.type="{ headers, item }">
          {{ getTypeName(item.type) }}
        </template>
        <template v-slot:item.dest_replace="{ headers, item }">
          {{
            item.dest_replace && item.dest_replace.startsWith("*86")
              ? $t("mailbox")
              : item.dest_replace
          }}
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="py-2">
            <call-forward
              v-model="item"
              :product-id="productId"
              :empty-call-forward="emptyCallForward"
              :types="types"
              :numbers="numbers"
              @cancel="expanded = []"
              @deleted="
                getItems();
                expanded = [];
              "
              @update="getItems()"
            />
          </td>
        </template>
      </data-table-extended>
    </v-card-text>
  </v-card>
</template>

<script>
import formatNumber from "@/utils/mixins/formatNumber";
import showErrors from "@/utils/mixins/showErrors";
import isMobile from "@/utils/mixins/isMobile";
import DataTableExtended from "@/components/basics/DataTableExtended";
import BooleanValue from "@/components/basics/BooleanValue";
import CallForward from "@/components/services/voip/CallForward";

export default {
  name: "CallForwards",
  mixins: [formatNumber, showErrors, isMobile],
  components: {
    DataTableExtended,
    BooleanValue,
    CallForward,
  },
  props: {
    productId: {
      validator: (prop) => typeof prop === "string" || typeof prop === "number",
    },
    numbers: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      items: [],
      expanded: [],
      loading: false,
      showAddCallForward: false,
      newItem: { ...this.emptyCallForward },
    };
  },
  watch: {
    productId: {
      immediate: true,
      handler(value) {
        this.getItems();
      },
    },
  },
  computed: {
    emptyCallForward() {
      return {
        id: null,
        name: null,
        type: "CFU",
        priority: 1,
        dest_pattern: null,
        source_pattern: null,
        source_presentation_pattern: null,
        dest_replace: null,
        time_pattern: null,
        reject_pattern: null,
        delay: 10,
        propagate_busy: false,
        parallel_call: false,
        always_ring: false,
        early_media: false,
        last_diversion: false,
        reroute: false,
        suspended: false,
        disabled: false,
        start_date: null,
        end_date: null,
        to_mailbox: false,
      };
    },
    headers() {
      return [
        {
          text: this.$i18n.t("active"),
          value: "disabled",
        },
        {
          text: this.$i18n.t("dest pattern"),
          value: "dest_pattern",
        },
        {
          text: this.$i18n.t("type"),
          value: "type",
        },
        {
          text: this.$i18n.t("dest replace"),
          value: "dest_replace",
        },
        { text: "", value: "data-table-expand" },
      ];
    },
    types() {
      return [
        { text: this.$i18n.t("CFU"), value: "CFU" },
        { text: this.$i18n.t("CFF"), value: "CFF" },
        { text: this.$i18n.t("CFB"), value: "CFB" },
        /* { text: this.$i18n.t("CFR"), value: "CFR" }, */
        { text: this.$i18n.t("CFNR"), value: "CFNR" },
        /* { text: this.$i18n.t("CFO"), value: "CFO" }, */
        /* { text: this.$i18n.t("DIST"), value: "DIST" }, */
        /* { text: this.$i18n.t("REDIR"), value: "REDIR" }, */
      ];
    },
  },
  methods: {
    getTypeName(value) {
      for (let i = 0; i < this.types.length; i++) {
        if (value == this.types[i].value) return this.types[i].text;
      }
      return value;
    },
    getItems() {
      var that = this;
      this.loading = true;
      this.$http
        .get("services/voip/" + this.productId + "/call-forwards")
        .then((response) => {
          that.items = response.data.map(function (cdr) {
            return {
              ...cdr,
              product_id: that.productId,
            };
          });
        })
        .catch((err) => {
          that.$store.commit("setSystemError", {
            msg: err.message,
            title: err.title,
          });
        })
        .finally(function () {
          that.loading = false;
        });
    },
  },
};
</script>

<i18n>
{
  "en": {
    "active": "Active",
    "call forwards": "Call forwards",
    "name": "Name",
    "delay": "delay",
    "dest pattern": "dest pattern",
    "dest replace": "dest replace",
    "source pattern": "source pattern",
    "source presentation pattern": "source presentation pattern",
    "add new call forward": "add new call forward",
    "type": "Type",
    "mailbox": "Mailbox",
    "disabled": "Disabled",
    "CFU": "CFU - permanent",
    "CFF": "CFF - not subscribed",
    "CFB": "CFB - busy",
    "CFR": "CFR - rejected",
    "CFNR": "CFNR - no answer",
    "CFO": "CFO - parallel call",
    "DIST": "DIST",
    "REDIR": "REDIR"
  },
  "de": {
    "active": "Aktiv",
    "call forwards": "Anrufweiterleitungen",
    "name": "Name",
    "delay": "Verzögerung",
    "dest pattern": "Rufnummer",
    "dest replace": "Umleitung auf",
    "source pattern": "source pattern",
    "source presentation pattern": "source presentation pattern",
    "add new call forward": "neue Anrufweiterleitung erstellen",
    "type": "Typ",
    "mailbox": "Mailbox",
    "disabled": "Deaktiviert",
    "CFU": "CFU - immer weiterleiten",
    "CFF": "CFF - wenn nicht erreichbar (Fallback)",
    "CFB": "CFB - wenn besetzt",
    "CFR": "CFR - abgelehnt",
    "CFNR": "CFNR - immer weiterleiten mit Verzögerung",
    "CFO": "CFO - paralleler Anruf",
    "DIST": "DIST",
    "REDIR": "REDIR"
  }
}
</i18n>
